import { pluralize } from '@cian/newbuilding-utils';
import { useBooleanState } from '@cian/react-utils';
import { Body1, Body2, Image } from '@cian/ui-kit';
import { numberToPrettyString } from '@cian/utils';

import { clsx } from 'clsx';
import { type FC, type MouseEvent as ReactMouseEvent, useCallback } from 'react';

import { type IPromo } from '../../../../types/promos';
import { PromoDetailsContainer } from '../../../PromoDetails';
import { PromoBlackFridayBadge } from '../PromoBlackFridayBadge';
import { TitleColumnRight } from '../TitleColumnRight';

import { getImageUrl, getInterestRate, isMortgageButtonAvailable, shortenMaxCredit } from './utils';

import styles from './styles.css';
import commonColumnStyles from '../common-column-styles.css';

export interface IMortgageOfferRowProps {
  monthlyPayment: number | null;
  mortgagePartnerLink: string;
  onClick: (promoInfo: IPromo) => void;
  onGetOnlineClick: (promoInfo: IPromo) => void;
  promo: IPromo;
}

export const MortgageOfferRow: FC<IMortgageOfferRowProps> = ({
  monthlyPayment,
  mortgagePartnerLink,
  onClick,
  onGetOnlineClick,
  promo,
}) => {
  const { name: promoName, bank, authorName, isBlackFriday } = promo;
  const { maxCredit, maxLoanTerm } = promo.mortgageParams || {};
  const { name: bankName } = bank || {};

  const {
    state: isPromoDetailsModalOpened,
    setFalse: setPromoDetailsModalClosed,
    setTrue: setPromoDetailsModalOpened,
  } = useBooleanState();

  const imageUrl = getImageUrl(promo);
  const isTitleRightVisible = isMortgageButtonAvailable(promo) || isBlackFriday;

  const handleClick = useCallback(() => {
    setPromoDetailsModalOpened();

    onClick(promo);
  }, [setPromoDetailsModalOpened, onClick, promo]);

  const handleGetOnlineClick = useCallback(
    (event: ReactMouseEvent) => {
      event.stopPropagation();
      onGetOnlineClick(promo);
    },
    [onGetOnlineClick, promo],
  );

  const maxCreditText = maxCredit ? <Body1>до {shortenMaxCredit(maxCredit)} ₽</Body1> : '—';
  const maxLoanTermText = maxLoanTerm ? <Body1>{pluralize(maxLoanTerm, ['год', 'года', 'лет'])}</Body1> : '—';
  const monthlyPaymentText = monthlyPayment ? <span>от {numberToPrettyString(monthlyPayment)} ₽/мес</span> : '—';

  return (
    <>
      <div className={styles['row']} data-testid="MortgageOfferRow" role="button" onClick={handleClick}>
        <div className={clsx(commonColumnStyles['common-title-col'], styles['title-col'])}>
          <div className={styles['image-and-title']}>
            <span className={styles['image-block']} data-testid="MortgageOfferRowImage">
              <Image borderRadius="50%" height={56} objectFit="contain" src={imageUrl} width={56} />
              {isBlackFriday && (
                <div className={styles['black-friday-badge']}>
                  <PromoBlackFridayBadge size={22} />
                </div>
              )}
            </span>
            <span className={styles['title-block']}>
              <div className={styles['promo-name']}>{promoName}</div>
              <Body2 color="icon-secondary-default">{bankName || authorName}</Body2>
            </span>
          </div>
          {isTitleRightVisible && (
            <div className={styles['title-column-right']}>
              <TitleColumnRight
                isBlackFridayPromo={isBlackFriday}
                isGetOnlineButtonAvailable={isMortgageButtonAvailable(promo)}
                mortgagePartnerLink={mortgagePartnerLink}
                onGetOnlineButtonClick={handleGetOnlineClick}
              />
            </div>
          )}
        </div>
        <span className={clsx(commonColumnStyles['common-rate-col'], styles['rate-col'])}>
          <Body1>{getInterestRate(promo)}</Body1>
        </span>
        <span className={clsx(commonColumnStyles['common-sum-col'], styles['sum-col'])}>{maxCreditText}</span>
        <span className={clsx(commonColumnStyles['common-term-col'], styles['term-col'])}>{maxLoanTermText}</span>
        <span className={clsx(commonColumnStyles['common-payment-col'], styles['payment-col'])}>
          <Body1 textAlign="right">{monthlyPaymentText}</Body1>
        </span>
      </div>
      {isPromoDetailsModalOpened && <PromoDetailsContainer promo={promo} onModalClose={setPromoDetailsModalClosed} />}
    </>
  );
};
